// Pure helpers for the History page. Nothing here calculates a CVI: every score is the value
// stored in a final snapshot (locked periods) or returned by the live server calculation
// (active period). Missing values stay null and are never treated as zero.

export const toNumber = value => (value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));

const normalizeDigits = value => String(value ?? "").replace(/[۰-۹]/g,d=>String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))).replace(/[٠-٩]/g,d=>String("٠١٢٣٤٥٦٧٨٩".indexOf(d)));
// Quarters are keyed q1–q4. The Persian terms are parsing aliases only, for period names stored
// before the Canadian release; they are never displayed.
export const seasonAliases = [
  { key:"q1", terms:["q1","quarter 1","t1","بهار","فصل اول","فصل 1","فصل یک"] },
  { key:"q2", terms:["q2","quarter 2","t2","تابستان","فصل دوم","فصل 2","فصل دو"] },
  { key:"q3", terms:["q3","quarter 3","t3","پاییز","پائيز","فصل سوم","فصل 3","فصل سه"] },
  { key:"q4", terms:["q4","quarter 4","t4","زمستان","فصل چهارم","فصل 4","فصل چهار"] },
];
export const QUARTER_RANK = { q1: 1, q2: 2, q3: 3, q4: 4 };

export const quarterLabel = (key, language) => `${language === "fr" ? "T" : "Q"}${QUARTER_RANK[key]}`;

// Calendar quarter (Gregorian) of the period start date.
function calendarParts(rawDate) {
    if (!rawDate) return null;
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return null;
    const seasonRank = Math.ceil((date.getUTCMonth() + 1) / 3);
    return { year: String(date.getUTCFullYear()), season: `q${seasonRank}`, seasonRank };
}

export function periodMeta(p, index = 0) {
    const fromDate = calendarParts(p?.startDate);
    if (fromDate) return { ...fromDate, time: new Date(p.endDate || p.startDate).getTime(), index };
    // Legacy fallback only when authoritative dates are unavailable.
    const label = normalizeDigits(p?.label || p?.name || "").toLowerCase();
    const year = (label.match(/(?:13|14|19|20)\d{2}/) || [])[0] || "";
    const season = seasonAliases.find(s => s.terms.some(t => label.includes(t)))?.key || "";
    return { year, season, seasonRank: QUARTER_RANK[season] || 0, time: 0, index };
}

export function periodDisplayLabel(p, language = "en") {
    const meta = periodMeta(p);
    if (p?.startDate && meta.year && meta.seasonRank) return `${quarterLabel(meta.season, language)} ${meta.year}`;
    const label = p?.label || p?.name || "";
    return language === "fr" ? label.replace(/\bQ([1-4])\b/g, "T$1") : label;
}

// "final" only for a locked period (server metadata wins); otherwise the result is provisional.
export function periodState(p) {
    if (p?.locked === true || String(p?.status || "").toLowerCase() === "locked") return "final";
    if (p?.locked === false || p?.provisional || ["temporary", "active", "live"].includes(String(p?.status || "").toLowerCase())) return "provisional";
    return p?.source === "live" ? "provisional" : "final";
}

const compareMeta = (a, b) => {
    if (a.time && b.time && a.time !== b.time) return a.time - b.time;
    const yd = Number(a.year || 0) - Number(b.year || 0); if (yd) return yd;
    const sd = a.seasonRank - b.seasonRank; if (sd) return sd;
    return a.index - b.index;
};

// Oldest → newest, each row carrying its metadata and display state.
export function chronological(periods = []) {
    return periods
        .map((p, index) => ({ p, meta: periodMeta(p, index) }))
        .sort((a, b) => compareMeta(a.meta, b.meta))
        .map(({ p, meta }) => ({ ...p, _meta: meta, _state: periodState(p) }));
}

// Difference with the most recent earlier *finalized* period that has a value. A provisional
// period is never compared with a finalized one: it reflects only the components assessed so far.
export function changeVsPrevious(chron = [], periodId, key = "cvi") {
    const index = chron.findIndex(row => String(row.periodId) === String(periodId));
    if (index < 0) return { value: null, previous: null, withheld: false };
    const current = chron[index];
    let previous = null;
    for (let i = index - 1; i >= 0; i -= 1) {
        if (chron[i]._state === "final" && toNumber(chron[i][key]) != null) { previous = chron[i]; break; }
    }
    if (current._state !== "final") return { value: null, previous, withheld: Boolean(previous) };
    const a = toNumber(current[key]);
    const b = toNumber(previous?.[key]);
    return { value: a == null || b == null ? null : a - b, previous, withheld: false };
}

// Default period to show: the latest finalized period with a CVI, else the latest with any value.
export function defaultFocusPeriod(chron = []) {
    for (let i = chron.length - 1; i >= 0; i -= 1) if (chron[i]._state === "final" && toNumber(chron[i].cvi) != null) return chron[i];
    for (let i = chron.length - 1; i >= 0; i -= 1) if (toNumber(chron[i].cvi) != null) return chron[i];
    return chron[chron.length - 1] || null;
}

export function formatScore(value, language = "en", digits = 1) {
    const n = toNumber(value);
    if (n == null) return "—";
    return new Intl.NumberFormat(language === "fr" ? "fr-CA" : "en-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

export function formatSigned(value, language = "en") {
    const n = toNumber(value);
    if (n == null) return "—";
    const sign = Math.abs(n) < 0.05 ? "±" : n > 0 ? "+" : "−";
    return `${sign}${formatScore(Math.abs(n), language)}`;
}

export function formatPercent(value, language = "en", digits = 0) {
    const n = toNumber(value);
    if (n == null) return "—";
    return `${formatScore(n, language, digits)}${language === "fr" ? " %" : "%"}`;
}
